import { toggleExpandedForAll } from 'react-sortable-tree';
import { v4 as uuidv4 } from 'uuid';

import { checkRepositoryName } from '../api/documentRepositoryConfiguration';
import {
  MAX_REPOSITORY_NAME_LENGTH,
  NEW_REPOSITORY_SAVED_SEARCH_ID,
  NEW_REPOSITORY_TEMPLATE,
  REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES,
} from '../constants/RepositoryConfigurationConstants';
import { DISTRIBUTION_TABLE_VALIDATION_PROPS } from '../constants/SmartInsightConstants';
import { WARNING_SOURCES } from '../constants/WarningsConstants';
import { translate } from '../form-designer/utils/form-manager.utils';
import { REPOSITORY_SAVED_SEARCH_TEMPLATE } from '../saved-search-designer/constants/new-saved-search-template';
import { modifySavedSearchOnSave } from '../saved-search-designer/utils/common.utils';

export const isValidName = (name = '') => {
  return RegExp('^(?:[a-zA-Z0-9|])*[^:*?"<>,\'%]*$').test(name);
};

export const validateRepositoryNameOnSaveAs = (searchName) => {
  if (!searchName) {
    return {
      hasError: true,
      errorText: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.REQUIRED,
    };
  }

  if (searchName.length > MAX_REPOSITORY_NAME_LENGTH) {
    return {
      hasError: true,
      errorText: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.MAX_LENGTH,
    };
  }

  if (!isValidName(searchName)) {
    return {
      hasError: true,
      errorText: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.SPECIAL_CHARACTERS,
    };
  }

  return { hasError: false, errorText: '' };
};

export const removeKeyFromTree = (tree, keyToRemove) => {
  return tree.map(({ [keyToRemove]: _, children, ...rest }) => ({
    ...rest,
    children: children ? removeKeyFromTree(children, keyToRemove) : [],
  }));
};

export const getExpandedNodeIds = (data = []) =>
  data.reduce((nodeIds, node) => {
    return node.childrenNodes?.length
      ? nodeIds.concat(node.id, ...getExpandedNodeIds(node.childrenNodes))
      : nodeIds;
  }, []);

export const filterTreeData = (tree, searchText) => {
  return tree.reduce((acc, node) => {
    const isSelectable = node.isSelectable !== false;

    const filteredChildren = node.childrenNodes
      ? filterTreeData(node.childrenNodes, searchText)
      : [];

    const fieldName = node.value.split('.')?.[1] || '';
    const textToCompare = searchText.toLowerCase();

    const matches =
      isSelectable &&
      (fieldName.toLowerCase().includes(textToCompare) ||
        node.label.toLowerCase().includes(textToCompare));

    if (matches || filteredChildren.length) {
      acc.push({ ...node, childrenNodes: filteredChildren });
    }

    return acc;
  }, []);
};

export const replaceTemplateText = (text, newValue) => text.replace(/\{\|.*?\|\}/g, newValue);

export const getNewRepositoryConfigData = () => ({
  savedSearches: {
    [NEW_REPOSITORY_SAVED_SEARCH_ID]: { savedSearch: REPOSITORY_SAVED_SEARCH_TEMPLATE },
  },
  repositoryDetails: {
    repositoryDetails: NEW_REPOSITORY_TEMPLATE,
    originalRepositoryDetails: NEW_REPOSITORY_TEMPLATE,
  },
  selectedSavedSearchId: NEW_REPOSITORY_SAVED_SEARCH_ID,
});

export const getExistingRepositoryConfigData = (repositoryConfig, repositoryId) => {
  repositoryConfig.viewDistribution.distributionList.forEach((item) => (item.rowId = item.id));
  repositoryConfig.adminDistribution.distributionList.forEach((item) => (item.rowId = item.id));

  const repositoryTree = repositoryConfig?.repositoryTree || [];

  const repositoryDetails = {
    ...repositoryConfig,
    repositoryTree: toggleExpandedForAll({ treeData: repositoryTree, expanded: true }),
    originalName: repositoryConfig.name,
    repositoryId,
  };

  return {
    savedSearches: repositoryConfig?.nodes || {},
    selectedSavedSearchId: repositoryConfig?.repositoryTree[0]?.nodeId || '',
    repositoryDetails: { repositoryDetails, originalRepositoryDetails: repositoryDetails },
  };
};

export const isFieldPropUnique = ({ items = [], prop, value }) => {
  const itemsByProp = items.filter((item) => item[prop]?.toUpperCase() === value?.toUpperCase());
  return itemsByProp.length === 1;
};

export const addError = ({
  validationErrors = [],
  translations = {},
  prop,
  instanceId = 'repositoryConfiguration',
  message = '',
}) => {
  validationErrors.push({
    errorId: uuidv4(),
    instanceId,
    controlInfo: {
      prop,
    },
    message: `${translate(translations, message)}.`,
    source: WARNING_SOURCES.VALIDATION,
  });
};

export const validateRepositoryName = async ({
  validationErrors = [],
  repositoryName = '',
  originalRepositoryName = '',
  translations = {},
}) => {
  if (!repositoryName) {
    addError({
      validationErrors,
      translations,
      prop: 'repositoryName',
      message: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.REQUIRED,
    });

    return validationErrors;
  }

  if (repositoryName.length > MAX_REPOSITORY_NAME_LENGTH) {
    addError({
      validationErrors,
      translations,
      prop: 'repositoryName',
      message: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.MAX_LENGTH,
    });

    return validationErrors;
  }

  if (!isValidName(repositoryName)) {
    addError({
      validationErrors,
      translations,
      prop: 'repositoryName',
      message: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.SPECIAL_CHARACTERS,
    });

    return validationErrors;
  }

  const isNameChanged = repositoryName !== originalRepositoryName;

  if (!isNameChanged) {
    return;
  }

  const savedSearchExistsResponse = await checkRepositoryName({ repositoryName });

  if (savedSearchExistsResponse.repositoryExists) {
    addError({
      validationErrors,
      translations,
      prop: 'repositoryName',
      message: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.UNIQUE,
    });
  }
};

export const validateRequired = ({
  item,
  validationDetails = {},
  validationErrors = [],
  translations = {},
}) => {
  if (validationDetails.required && !item[validationDetails?.prop]) {
    addError({
      validationErrors,
      translations,
      instanceId: item.rowId,
      prop: validationDetails?.prop,
      message: `${validationDetails.errorLabel} in ${validationDetails.tableName} is required`,
    });
  }
};

export const validateUnique = ({
  items = [],
  item,
  validationDetails = {},
  validationErrors = [],
  translations = {},
}) => {
  if (
    validationDetails.unique &&
    item[validationDetails?.prop] &&
    !isFieldPropUnique({
      items,
      prop: validationDetails?.prop,
      value: item[validationDetails?.prop],
    })
  ) {
    addError({
      validationErrors,
      translations,
      instanceId: item.rowId,
      prop: validationDetails?.prop,
      message: `${validationDetails.errorLabel} in ${validationDetails.tableName} should be unique`,
    });
  }
};

export const validateControls = ({
  items = [],
  controlsValidationProps = [],
  validationErrors = [],
  translations = {},
}) => {
  items.forEach((item) => {
    controlsValidationProps.forEach((validationDetails) => {
      validateRequired({ item, validationDetails, validationErrors, translations });
      validateUnique({ items, item, validationDetails, validationErrors, translations });
    });
  });
};

export const validateRepositoryOnSave = async ({
  translations = {},
  validateSearchName = false,
  repositoryDetails = {},
}) => {
  const validationErrors = [];

  if (validateSearchName) {
    await validateRepositoryName({
      validationErrors,
      repositoryName: repositoryDetails.name,
      originalRepositoryName: repositoryDetails.originalName,
      translations,
    });
  }

  return validationErrors;
};

export const validateDistributionOnSave = ({ translations = {}, repositoryDetails = {} }) => {
  const validationErrors = [];

  validateControls({
    items: repositoryDetails.viewDistribution?.distributionList,
    controlsValidationProps: DISTRIBUTION_TABLE_VALIDATION_PROPS,
    validationErrors,
    translations,
  });

  validateControls({
    items: repositoryDetails.adminDistribution?.distributionList,
    controlsValidationProps: DISTRIBUTION_TABLE_VALIDATION_PROPS,
    validationErrors,
    translations,
  });

  return validationErrors;
};

export const modifyRepositoryNodesForSave = (nodes, entityFields) =>
  Object.keys(nodes).reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        savedSearch: modifySavedSearchOnSave(nodes[key].savedSearch, entityFields),
      },
    }),
    {}
  );
